import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
} from "@progress/kendo-react-grid";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography, getter } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import ShadowCard from "../../components/common/ShadowCard";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import {
  createTarget,
  getTargetByID,
  updateTarget,
} from "./services/target.services";
import { clearTargetDetails } from "./targetSlice";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormDatePicker from "../../components/formFields/FormDateField";
import { getAllSalesRepresentative } from "../salesrepresentativeassign/services/salesRepresentative.services";
import {
  getSelectedState,
  HeaderThElement,
} from "@progress/kendo-react-data-tools";
import moment from "moment";
import { findAllAreaAssignDistributor } from "../areaAssignToDistributor/services/areaAssignToDistributor.services";
import { ErrorToast } from "../../components/toast/Toasts";

interface ChangeWatcherProps {
  formRenderProps: FormRenderProps;
}
const DistributorChangeWatcher: React.FC<ChangeWatcherProps> = ({
  formRenderProps,
}) => {
  useEffect(() => {
    formRenderProps.onChange("abcd", { value: "" });
  }, []);
  return null;
};

const CreateTarget: React.FC = () => {
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const target_set_guid = location.state?.target_set_guid;
  const SalesRepresentativeList = useAppSelector(
    (state) => state.salesrepresentative.SalesRepresentativeList
  );

  const [selectedState, setSelectedState] = React.useState<{
    [id: string]: boolean | number[];
  }>({});
  const [rowSelectionModel, setRowSelectionModel] = React.useState<number[]>(
    []
  );
  const DistributorList = useAppSelector(
    (state) => state.areaAssign.DistributorList
  );

  useEffect(() => {
    setRowSelectionModel([]);
    dispatch(findAllAreaAssignDistributor());
    dispatch(getAllSalesRepresentative());
    return () => {
      dispatch(clearTargetDetails());
    };
  }, []);

  const loading = useAppSelector((state) => state.target.loading);
  const TargetDetail = useAppSelector((state) => state.target.TargetDetail);

  useEffect(() => {
    if (target_set_guid) {
      const payload = {
        target_set_guid: target_set_guid,
      };
      dispatch(getTargetByID(payload));
    }
  }, [target_set_guid]);

  // useEffect(() => {
  //   if (target_set_guid) {
  //     setRowSelectionModel(TargetDetail?.distributor_id);
  //   }
  // }, [TargetDetail, target_set_guid]);

  const handleSubmit = async (values: any) => {
    if (!target_set_guid && rowSelectionModel.length === 0)
      return ErrorToast("Please select at least one distributor!");
    if (target_set_guid) {
      try {
        const editPayload = {
          target_set_guid: target_set_guid,
          from_date: values?.from_date
            ? moment(values?.from_date).format("YYYY-MM-DD")
            : "",
          to_date: values?.to_date
            ? moment(values?.to_date).format("YYYY-MM-DD")
            : "",
          sr_id: values.sr_id ? values.sr_id : null,
          basic_per: values.basic_per ? values.basic_per : null,
          target_per: values.target_per ? values.target_per : null,
          distributor_id: values?.distributor_id,
        };
        const response = await dispatch(updateTarget(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/target");
          dispatch(closed());
          dispatch(clearTargetDetails());
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
      }
    } else {
      try {
        const insertPayload = {
          from_date: values?.from_date
            ? moment(values?.from_date).format("YYYY-MM-DD")
            : "",
          to_date: values?.to_date
            ? moment(values?.to_date).format("YYYY-MM-DD")
            : "",
          sr_id: values.sr_id ? values.sr_id : null,
          basic_per: values.basic_per ? values.basic_per : null,
          target_per: values.target_per ? values.target_per : null,
          distributor_id:
            rowSelectionModel.length > 0 ? rowSelectionModel.join(",") : "",
        };
        const response = await dispatch(createTarget(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/target");
          dispatch(closed());
          dispatch(clearTargetDetails());
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
    navigate("/target");
  };

  // FOR GRID

  const dataState = DistributorList?.map((dataItem: any) =>
    Object.assign({ selected: false }, dataItem)
  );
  const DATA_ITEM_KEY: string = "id";
  const SELECTED_FIELD: string = "selected";
  const idGetter = getter(DATA_ITEM_KEY);
  const onSelectionChange = React.useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedState(newSelectedState);
      const trueKeys = Object.keys(newSelectedState)
        .filter((key) => newSelectedState[key])
        .map(Number);
      setRowSelectionModel(trueKeys);
    },
    [selectedState]
  );

  const onHeaderSelectionChange = React.useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState: { [key: string]: boolean } = {};

      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
      const trueKeys = Object.keys(newSelectedState)
        .filter((key: string) => newSelectedState[key])
        .map(Number);
      setRowSelectionModel(trueKeys);
    },
    []
  );
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );
  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      <Form
        onSubmit={handleSubmit}
        initialValues={TargetDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {target_set_guid ? "Update Target" : "Add Target"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    name="from_date"
                    label="From Date"
                    format={"dd/MM/yyyy"}
                    component={FormDatePicker}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    name="to_date"
                    label="To Date"
                    format={"dd/MM/yyyy"}
                    minDate={
                      formRenderProps.valueGetter("from_date") ||
                      new Date(1900, 1, 1)
                    }
                    component={FormDatePicker}
                  />
                </GridLayoutItem>
                {/* <GridLayoutItem></GridLayoutItem> */}
                <GridLayoutItem>
                  <Field
                    name="sr_id"
                    label="Sales Representative"
                    placeholder="Select Sales Representative"
                    component={FormSelectionField}
                    astrike={true}
                    validator={requiredValidator}
                    options={SalesRepresentativeList.map((emp: any) => ({
                      value: emp.id,
                      label: `${emp?.first_name} ${emp?.middle_name} ${emp?.last_name}`,
                    }))}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    name="basic_per"
                    label="Basic %"
                    type="number"
                    max={100}
                    min={0}
                    component={FormNumericTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    name="target_per"
                    label="Target %"
                    type="number"
                    max={100}
                    min={0}
                    component={FormNumericTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem></GridLayoutItem>
                {target_set_guid && (
                  <GridLayoutItem>
                    <Field
                      name="distributor_id"
                      label="Distributor"
                      wrapperStyle={{ margin: "10px 0" }}
                      placeholder="Select Distributor"
                      component={FormSelectionField}
                      options={DistributorList.map((distributor: any) => ({
                        value: distributor.id,
                        label: distributor.account_name,
                      }))}
                      validator={requiredValidator}
                      astrike={true}
                      // disabled={true}
                    />
                  </GridLayoutItem>
                )}
                {!target_set_guid && (
                  <GridLayoutItem colSpan={2} style={{ marginTop: 20 }}>
                    <KendoGrid
                      style={{ maxHeight: "calc(100vh - 40vh)" }}
                      className="responsive-table"
                      filterable={true}
                      filter={filter}
                      data={
                        filter
                          ? filterBy(DistributorList, filter)?.map(
                              (item: any) => ({
                                ...item,
                                [SELECTED_FIELD]: selectedState[idGetter(item)],
                              })
                            )
                          : DistributorList?.map((item: any) => ({
                              ...item,
                              [SELECTED_FIELD]: selectedState[idGetter(item)],
                            }))
                      }
                      onFilterChange={handleFilterChange}
                      selectedField={SELECTED_FIELD}
                      onSelectionChange={onSelectionChange}
                      onHeaderSelectionChange={onHeaderSelectionChange}
                      cells={{
                        headerCell: HeaderCustomCell,
                        data: MyDataCustomCell,
                      }}
                    >
                      <Column
                        filterable={false}
                        field={SELECTED_FIELD}
                        width={50}
                        headerSelectionValue={
                          dataState?.findIndex(
                            (item: any) => !selectedState[idGetter(item)]
                          ) === -1
                        }
                      />
                      <Column
                        field="account_name"
                        title="Distributor"
                        cell={(props: any) => (
                          <td>
                            <div>{`${props.dataItem?.account_name?.toUpperCase()}`}</div>
                          </td>
                        )}
                      />
                      <Column
                        field="city_name"
                        title="City"
                        cell={(props: any) => (
                          <td>
                            <div>{`${props.dataItem?.city_name?.toUpperCase()}`}</div>
                          </td>
                        )}
                      />
                    </KendoGrid>
                  </GridLayoutItem>
                )}

                <GridLayoutItem
                  colSpan={3}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={!target_set_guid ? "Save" : "Update"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 4 }}
                    onClick={handleFormClose}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <DistributorChangeWatcher formRenderProps={formRenderProps} />
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateTarget;
